/**
 * Iris data manifest types & loader.
 *
 * The manifest declares partitioned data files per region. The loader fetches
 * them all in parallel, tags each entity with its source region, and returns
 * a flat dataset that the rest of the app consumes - without ever needing to
 * know how the data on disk is split. This is what makes the data layer
 * sustainable as we add more regions / countries / domains.
 */

export interface DatasetManifest {
  version: string;
  description: string;
  regions: RegionManifest[];
  shared: {
    threat_actors: string;
    threat_campaigns: string;
    vulnerabilities: string;
    compliance: string;
    vessels: string;
    focus_modes: string;
  };
}

export interface RegionManifest {
  id: string;
  label: string;
  country_codes: string[];
  default_bounds: [[number, number], [number, number]];
  default_center: [number, number];
  default_zoom: number;
  datasets: {
    assets: string[];
    dependencies: string[];
    alerts: string[];
    intelligence: string[];
    ot_devices: string[];
    policies: string[];
    baselines: string[];
    sensors: string[];
    scenarios: string[];
    compliance: string[];
  };
  compliance_frameworks: string[];
}

export interface FocusMode {
  id: string;
  label: string;
  short_label: string;
  description: string;
  regions: string[];
  country_codes: string[] | null;
  bounds: [[number, number], [number, number]];
  center: [number, number];
  zoom: number;
  emphasis: {
    threat_actors: string[];
    sectors: string[];
    campaigns: string[];
  };
  applicable_frameworks: string[];
}

const BASE = '/data';

async function fetchJson<T>(path: string): Promise<T> {
  const r = await fetch(`${BASE}/${path}`);
  if (!r.ok) throw new Error(`Failed to fetch ${path}: ${r.status}`);
  return r.json();
}

async function fetchAll<T>(paths: string[]): Promise<T[]> {
  if (paths.length === 0) return [];
  const arrays = await Promise.all(paths.map((p) => fetchJson<T[]>(p)));
  return arrays.flat();
}

export interface RegionTag {
  region_id: string;
}

/**
 * Run the loader. Returns a flat normalised dataset with each entity tagged
 * by its source region. The store calls this once at app start.
 */
export async function loadAll() {
  const manifest = await fetchJson<DatasetManifest>('manifest.json');

  const [actors, campaigns, vulns, sharedComp, vessels, focusModes] =
    await Promise.all([
      fetchJson<unknown[]>(manifest.shared.threat_actors),
      fetchJson<unknown[]>(manifest.shared.threat_campaigns),
      fetchJson<unknown[]>(manifest.shared.vulnerabilities),
      fetchJson<unknown[]>(manifest.shared.compliance),
      fetchJson<unknown[]>(manifest.shared.vessels),
      fetchJson<FocusMode[]>(manifest.shared.focus_modes),
    ]);

  type Tagged<T> = T & RegionTag;
  type Bag = Record<string, unknown>;

  const allAssets: Tagged<Bag>[] = [];
  const allDeps: Tagged<Bag>[] = [];
  const allAlerts: Tagged<Bag>[] = [];
  const allIntel: Tagged<Bag>[] = [];
  const allOt: Tagged<Bag>[] = [];
  const allPolicies: Tagged<Bag>[] = [];
  const allBaselines: Tagged<Bag>[] = [];
  const allSensors: Tagged<Bag>[] = [];
  const allScenarios: Tagged<Bag>[] = [];
  const regionalCompliance: Tagged<Bag>[] = [];

  await Promise.all(
    manifest.regions.map(async (region) => {
      const tag = (rows: Bag[]): Tagged<Bag>[] =>
        rows.map((r) => ({ ...r, region_id: region.id }) as Tagged<Bag>);

      const [a, d, al, in_, ot, pol, bl, sn, sc, comp] = await Promise.all([
        fetchAll<Bag>(region.datasets.assets),
        fetchAll<Bag>(region.datasets.dependencies),
        fetchAll<Bag>(region.datasets.alerts),
        fetchAll<Bag>(region.datasets.intelligence),
        fetchAll<Bag>(region.datasets.ot_devices),
        fetchAll<Bag>(region.datasets.policies),
        fetchAll<Bag>(region.datasets.baselines),
        fetchAll<Bag>(region.datasets.sensors),
        fetchAll<Bag>(region.datasets.scenarios),
        fetchAll<Bag>(region.datasets.compliance),
      ]);
      allAssets.push(...tag(a));
      allDeps.push(...tag(d));
      allAlerts.push(...tag(al));
      allIntel.push(...tag(in_));
      allOt.push(...tag(ot));
      allPolicies.push(...tag(pol));
      allBaselines.push(...tag(bl));
      allSensors.push(...tag(sn));
      allScenarios.push(...tag(sc));
      regionalCompliance.push(...tag(comp));
    }),
  );

  const taggedSharedComp = (sharedComp as Bag[]).map(
    (c) => ({ ...c, region_id: 'global' }) as Tagged<Bag>,
  );
  const allCompliance = [...taggedSharedComp, ...regionalCompliance];

  const taggedActors = (actors as Bag[]).map(
    (a) => ({ ...a, region_id: 'global' }) as Tagged<Bag>,
  );
  const taggedCampaigns = (campaigns as Bag[]).map(
    (c) => ({ ...c, region_id: 'global' }) as Tagged<Bag>,
  );
  const taggedVulns = (vulns as Bag[]).map(
    (v) => ({ ...v, region_id: 'global' }) as Tagged<Bag>,
  );
  const taggedVessels = (vessels as Bag[]).map(
    (v) => ({ ...v, region_id: 'global' }) as Tagged<Bag>,
  );

  return {
    manifest,
    focusModes,
    assets: allAssets,
    dependencies: allDeps,
    alerts: allAlerts,
    intelligence: allIntel,
    otDevices: allOt,
    policies: allPolicies,
    baselines: allBaselines,
    sensors: allSensors,
    scenarios: allScenarios,
    compliance: allCompliance,
    actors: taggedActors,
    campaigns: taggedCampaigns,
    vulnerabilities: taggedVulns,
    vessels: taggedVessels,
  };
}
